import type { TextFieldProps } from "@mui/material/TextField";
import { FormHelperText, Box } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/de";
import {
  TextInput,
  AutocompleteInput,
  ReferenceInput,
  RadioButtonGroupInput,
  type RadioButtonGroupInputProps,
  type TextInputProps,
  type AutocompleteInputProps,
  type ReferenceInputProps,
} from "react-admin";

import type { RaRecord, InputProps } from "react-admin";
import { useInput } from "react-admin";

export const appFieldHeight = 48;

export const appInputBaseSx = {
  "& .RaInput-root": {
    marginTop: 0,
    marginBottom: 0,
  },
  "& .MuiFormControl-root": {
    margin: 0,
  },
  "& .MuiInputLabel-root": {
    fontSize: 14,
  },
  "& .MuiFormHelperText-root": {
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    fontSize: 14,
  },
};

export const appTextFieldSx = {
  ...appInputBaseSx,
  "& .MuiInputBase-root": {
    minHeight: appFieldHeight,
    backgroundColor: "common.white",
    borderRadius: 1,
    alignItems: "center",
    fontSize: 14,
  },
  "& .MuiOutlinedInput-input": {
    fontSize: 14,
    lineHeight: 1.5,
    boxSizing: "border-box",
    paddingTop: "12px",
    paddingBottom: "12px",
  },
  "& .MuiAutocomplete-input": {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
  },
  "& .MuiInputBase-multiline": {
    minHeight: "auto",
    alignItems: "stretch",
  },
  "& .MuiInputBase-multiline textarea": {
    minHeight: "auto !important",
    paddingTop: 0,
    paddingBottom: 0,
    lineHeight: 1.5,
  },
};

export const appChoiceFieldSx = {
  ...appInputBaseSx,
  "&.RaRadioButtonGroupInput-root": {
    marginTop: 0,
    marginBottom: 0,
  },
  "& .MuiFormLabel-root": {
    fontSize: 28,
    color: "text.primary",
  },
  "& .MuiFormGroup-root": {
    gap: 0,
  },
  "& .MuiFormControlLabel-root": {
    marginTop: 0,
    marginBottom: 0,
  },
  "& .MuiFormControlLabel-label": {
    fontSize: 14,
  },
};

export const appTextFieldProps: Pick<
  TextFieldProps,
  "variant" | "size" | "fullWidth"
> = {
  variant: "outlined",
  size: "small",
  fullWidth: true,
};

export function AppTextInput(props: TextInputProps) {
  const { sx, slotProps, ...rest } = props;

  return (
    <TextInput
      {...rest}
      {...appTextFieldProps}
      helperText={props.helperText ?? false}
      sx={{
        ...appTextFieldSx,
        ...sx,
      }}
      slotProps={slotProps}
    />
  );
}

export function AppAutocompleteInput(props: AutocompleteInputProps) {
  const { sx, ...rest } = props;

  return (
    <AutocompleteInput
      {...rest}
      {...appTextFieldProps}
      helperText={props.helperText ?? false}
      sx={{
        ...appTextFieldSx,
        ...sx,
      }}
    />
  );
}

type AppReferenceInputProps = ReferenceInputProps & {
  autocompleteProps?: AutocompleteInputProps;
};

export function AppReferenceInput({
  autocompleteProps,
  label,
  ...props
}: AppReferenceInputProps) {
  return (
    <ReferenceInput {...props} label={label}>
      <AppAutocompleteInput
        label={label}
        helperText={autocompleteProps?.helperText ?? false}
        {...autocompleteProps}
      />
    </ReferenceInput>
  );
}

type MuiMargin = "none" | "dense" | "normal";

type DateTimeInputDeProps<RecordType extends RaRecord = any> =
  InputProps<RecordType> & {
    displayFormat?: string;
    ampm?: boolean;
    fullWidth?: boolean;
    margin?: MuiMargin;
    withLocalizationProvider?: boolean;
  };

function toDayjs(value: unknown): Dayjs | null {
  if (value == null || value === "") return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }
  if (typeof value === "string") {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }
  return null;
}

export function AppDateTimeInputDe<RecordType extends RaRecord = any>(
  props: DateTimeInputDeProps<RecordType>,
) {
  const {
    source,
    label,
    helperText,
    validate,
    disabled,
    displayFormat = "DD/MM/YYYY HH:mm",
    ampm = false,
    fullWidth = true,
    ...rest
  } = props;

  const {
    field,
    fieldState: { error, invalid },
    id,
    isRequired,
  } = useInput({
    source,
    validate,
    disabled,
    ...rest,
  });

  const value = toDayjs(field.value);
  const showError = invalid && !!error?.message;

  return (
    <Box sx={appTextFieldSx}>
      <DateTimePicker
        value={value}
        onChange={(newValue) => {
          if (!newValue || !newValue.isValid()) {
            field.onChange(null);
            return;
          }
          field.onChange(newValue.toDate());
        }}
        disabled={disabled}
        format={displayFormat}
        ampm={ampm}
        slotProps={{
          textField: {
            id,
            label,
            required: isRequired,
            fullWidth,
            size: "small",
            error: showError,
            helperText: false,
            InputLabelProps: { shrink: true },
            onBlur: field.onBlur,
          },
        }}
      />
      {(showError || helperText) && (
        <FormHelperText
          error={showError}
          sx={{
            mt: 0,
            ml: 0,
            mr: 0,
          }}
        >
          {showError ? error?.message : helperText}
        </FormHelperText>
      )}
    </Box>
  );
}

export function AppRadioButtonGroupInput(
  props: RadioButtonGroupInputProps,
) {
  const { sx, ...rest } = props;

  return (
    <RadioButtonGroupInput
      {...rest}
      sx={{ ...appChoiceFieldSx, ...sx }}
      helperText={props.helperText ?? false}
    />
  );
}
